import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useLeagueZones, type _LeagueAvailabilityZone } from "@/hooks/useLeagueZones";
import { format, addDays } from "date-fns";
import { getLocalDayBounds } from "@/lib/utils";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────────────

export interface CalendarBooking {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

export interface CalendarMatch {
  id: string;
  round: number;
  division_id: string;
  division_name: string;
  division_level: number;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court_id: string | null;
  status: string;
  home_team_id: string;
  away_team_id: string;
  home_label: string;
  away_label: string;
}

export interface UnplacedGroup {
  division_id: string;
  division_name: string;
  division_level: number;
  round: number;
  matches: CalendarMatch[];
}

// ── Helpers ────────────────────────────────────────────────────────────

function buildTeamLabel(team: {
  team_name: string | null;
  p1: { full_name: string | null } | null;
  p2: { full_name: string | null } | null;
} | null): string {
  if (!team) return "?";
  if (team.team_name) return team.team_name;
  const p1 = team.p1?.full_name?.split(" ")[0] ?? "?";
  const p2 = team.p2?.full_name?.split(" ")[0] ?? "?";
  return `${p1}/${p2}`;
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useLeagueCalendarData(leagueId: string | undefined, weekStart: Date) {
  const queryClient = useQueryClient();
  const { hallId } = useOrgHall();
  const { zones } = useLeagueZones(leagueId);

  // Week date range
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekStartStr = format(weekDates[0], "yyyy-MM-dd");
  const weekEndStr = format(weekDates[6], "yyyy-MM-dd");

  // ── Bookings for the week (background layer) ────────────────────────

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery<CalendarBooking[]>({
    queryKey: ["league-cal-bookings", hallId, weekStartStr],
    queryFn: async () => {
      const { start } = getLocalDayBounds(weekDates[0]);
      const { end } = getLocalDayBounds(weekDates[6]);
      const { data, error } = await supabase
        .from("bookings")
        .select("id, court_id, start_time, end_time, status")
        .gte("start_time", start)
        .lte("start_time", end)
        .neq("status", "cancelled");
      if (error) throw error;
      return (data ?? []) as CalendarBooking[];
    },
    enabled: !!hallId,
  });

  // ── All league matches (foreground + unplaced) ───────────────────────

  const { data: leagueMatches = [], isLoading: matchesLoading } = useQuery<CalendarMatch[]>({
    queryKey: ["league-cal-matches", leagueId],
    queryFn: async () => {
      // 1. Fetch matches
      const { data: rawMatches, error } = await supabase
        .from("league_matches")
        .select("*")
        .eq("league_id", leagueId!)
        .in("status", ["scheduled", "rescheduled"]);
      if (error) throw error;
      if (!rawMatches || rawMatches.length === 0) return [];

      // 2. Fetch divisions for names
      const divIds = [...new Set(rawMatches.map((m) => m.division_id))];
      const { data: divData } = await supabase
        .from("divisions")
        .select("id, name, level")
        .in("id", divIds);
      const divMap = Object.fromEntries(
        (divData ?? []).map((d) => [d.id, { name: d.name as string, level: d.level as number }]),
      );

      // 3. Fetch teams + profiles
      // TASK-67 PR-D: roster sourced from team_members embed; the first
      // 2 primaries by joined_at form the canonical pair for the team
      // label (matches PR-A's sync_league_match_to_match_results trigger
      // semantics). Replaces direct teams.player1/2_id reads.
      const teamIds = [...new Set(rawMatches.flatMap((m) => [m.home_team_id, m.away_team_id]))];
      if (teamIds.length === 0) return [];
      const { data: teamsData } = await supabase
        .from("teams")
        .select(`
          id, team_name,
          members:team_members(user_id, customer_id, role, joined_at)
        `)
        .in("id", teamIds);
      type TeamWithMembers = {
        id: string;
        team_name: string | null;
        members: Array<{ user_id: string | null; role: "primary" | "reserve"; joined_at: string }> | null;
      };
      const teamRows = ((teamsData ?? []) as unknown as TeamWithMembers[]) ?? [];
      const playerIds = [
        ...new Set(
          teamRows.flatMap((t) =>
            (t.members ?? [])
              .filter((m) => m.role === "primary" && m.user_id)
              .map((m) => m.user_id as string),
          ),
        ),
      ];
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .in("id", playerIds);
      const profileMap = Object.fromEntries((profiles ?? []).map((p) => [p.id, p]));
      const teamMap = Object.fromEntries(
        teamRows.map((t) => {
          const primaries = (t.members ?? [])
            .filter((m) => m.role === "primary" && m.user_id)
            .sort((a, b) => a.joined_at.localeCompare(b.joined_at));
          return [
            t.id,
            {
              team_name: t.team_name,
              p1: profileMap[primaries[0]?.user_id ?? ""] ?? null,
              p2: profileMap[primaries[1]?.user_id ?? ""] ?? null,
            },
          ];
        }),
      );

      return rawMatches.map((m) => ({
        id: m.id as string,
        round: m.round as number,
        division_id: m.division_id as string,
        division_name: divMap[m.division_id]?.name ?? "?",
        division_level: divMap[m.division_id]?.level ?? 0,
        scheduled_date: m.scheduled_date as string | null,
        scheduled_time: m.scheduled_time as string | null,
        court_id: m.court_id as string | null,
        status: m.status as string,
        home_team_id: m.home_team_id as string,
        away_team_id: m.away_team_id as string,
        home_label: buildTeamLabel(teamMap[m.home_team_id] ?? null),
        away_label: buildTeamLabel(teamMap[m.away_team_id] ?? null),
      }));
    },
    enabled: !!leagueId,
  });

  // ── Divisions for this league ────────────────────────────────────────

  const { data: divisions = [] } = useQuery<{ id: string; name: string; level: number }[]>({
    queryKey: ["league-cal-divisions", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("divisions")
        .select("id, name, level")
        .eq("league_id", leagueId!)
        .order("level");
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; level: number }[];
    },
    enabled: !!leagueId,
  });

  // ── Derived: matches placed during this week ─────────────────────────

  const weekMatches = useMemo(
    () =>
      leagueMatches.filter(
        (m) => m.scheduled_date && m.scheduled_date >= weekStartStr && m.scheduled_date <= weekEndStr,
      ),
    [leagueMatches, weekStartStr, weekEndStr],
  );

  // ── Derived: unplaced matches grouped by division + round ────────────

  const unplacedMatches = useMemo(() => {
    const unplaced = leagueMatches.filter((m) => !m.scheduled_date);
    const groupKey = (m: CalendarMatch) => `${m.division_id}__${m.round}`;
    const map = new Map<string, UnplacedGroup>();
    for (const m of unplaced) {
      const key = groupKey(m);
      if (!map.has(key)) {
        map.set(key, {
          division_id: m.division_id,
          division_name: m.division_name,
          division_level: m.division_level,
          round: m.round,
          matches: [],
        });
      }
      map.get(key)!.matches.push(m);
    }
    return [...map.values()].sort(
      (a, b) => a.division_level - b.division_level || a.round - b.round,
    );
  }, [leagueMatches]);

  // ── Mutations ────────────────────────────────────────────────────────

  const placeMatch = useMutation({
    mutationFn: async ({
      matchId,
      courtId,
      date,
      time,
    }: {
      matchId: string;
      courtId: string;
      date: string;
      time: string;
    }) => {
      const { error } = await (supabase as any)
        .from("league_matches")
        .update({ court_id: courtId, scheduled_date: date, scheduled_time: time })
        .eq("id", matchId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-cal-matches", leagueId] });
      toast.success("Match placerad!");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte placera matchen");
    },
  });

  const unplaceMatch = useMutation({
    mutationFn: async (matchId: string) => {
      const { error } = await (supabase as any)
        .from("league_matches")
        .update({ court_id: null, scheduled_date: null, scheduled_time: null })
        .eq("id", matchId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-cal-matches", leagueId] });
      toast.success("Match borttagen från schemat");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte ta bort matchen");
    },
  });

  return {
    bookings,
    weekMatches,
    leagueMatches,
    unplacedMatches,
    divisions,
    zones,
    placeMatch,
    unplaceMatch,
    isLoading: bookingsLoading || matchesLoading,
  };
}
